import {
  Video,
  VideoDeletionResponse,
  VideoDeletionStatus,
} from '../services/api';

export interface PendingVideoDeletion {
  deletion_id: string;
  status: VideoDeletionStatus;
  retry_after_seconds?: number | null;
  video_title: string;
  initiated_at: string;
}

export type PendingVideoDeletionMap = Record<string, PendingVideoDeletion>;

export const excludeAcceptedVideoDeletions = <T extends Pick<Video, 'video_id'>>(
  videos: T[],
  acceptedDeletionIds: ReadonlySet<string>,
): T[] => videos.filter(
  (video) => !acceptedDeletionIds.has(video.video_id),
);

export const recordAcceptedVideoDeletion = (
  current: PendingVideoDeletionMap,
  response: VideoDeletionResponse,
  videoTitle: string,
  initiatedAt: string,
): PendingVideoDeletionMap => {
  const next = { ...current };
  if (response.status === 'deleted') {
    delete next[response.video_id];
    return next;
  }
  next[response.video_id] = {
    deletion_id: response.deletion_id,
    status: response.status,
    retry_after_seconds: response.retry_after_seconds,
    video_title: videoTitle,
    initiated_at: initiatedAt,
  };
  return next;
};

export const applyVideoDeletionStatus = (
  current: PendingVideoDeletionMap,
  response: VideoDeletionResponse,
): PendingVideoDeletionMap => {
  const existing = current[response.video_id];
  if (!existing || existing.deletion_id !== response.deletion_id) {
    return current;
  }
  const next = { ...current };
  if (response.status === 'deleted') {
    delete next[response.video_id];
    return next;
  }
  next[response.video_id] = {
    ...existing,
    status: response.status,
    retry_after_seconds: response.retry_after_seconds,
  };
  return next;
};

export const clearVideoDeletionIfMatching = (
  current: PendingVideoDeletionMap,
  videoId: string,
  deletionId: string,
): PendingVideoDeletionMap => {
  if (current[videoId]?.deletion_id !== deletionId) {
    return current;
  }
  const next = { ...current };
  delete next[videoId];
  return next;
};
